import { Container, Sprite, Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import { arrayRandom } from '../../../lib/util/jsTools';
import { config } from '../../defs/config';
import {
  spawnBlockEntity,
  ParcelsBlockEntity,
  despawnBlockEntity,
  BlockEntity,
  BasicBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import {
  blockIterateAll,
  cellHasBasicAndNoOverlay,
} from '../../util/blockTools';
import { mapIterateCells, mapToViewPosition } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { BreakEffect } from './BreakEffect';
import { BasicBlockProps, blockColorMap } from '../../defs/block';
import { createBlockView } from '../../../editor/support/blockViewFactory';

export type ParcelsEffectOptions = {
  subject: ParcelsBlockEntity;
};

/*
  spawns one new parcel from the position of the parcels at a random (non parcels) block
*/
export class ParcelsEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly options: ParcelsEffectOptions;

  constructor(scene: GameScene, options: ParcelsEffectOptions) {
    this.scene = scene;
    this.options = options;
  }

  public static assets(): string[] {
    return [];
  }

  public async execute() {
    const state = this.scene.sessionEntity.c.state;

    // require throws remaining
    if (state.parcelThrowsRemaining === 0) return false;

    // pick a target
    const target = this.selectTarget();
    if (!target) return false;
    const targetPosition = target.c.position.mapPosition;

    // Decrement throws remaining in session state
    state.parcelThrowsRemaining -= 1;

    // spawn parcel at target position
    const parcel = spawnBlockEntity(
      this.scene,
      { id: 'hatAll' },
      targetPosition,
    );

    // Set the parcel to be invisible while animating
    parcel.c.view2d.view.visible = false;

    // Get the target view position
    const to = mapToViewPosition(targetPosition);

    // animate parcel throw
    await this.animateParcelSpawn(to);

    // despawn occupied target
    despawnBlockEntity(this.scene, target, true);

    // TODO: SFX HERE ------------------------

    // play block break effect
    const colorId = (target.c.block.props as BasicBlockProps).color;
    const color = blockColorMap[colorId].base;
    await new BreakEffect(this.scene, {
      position: targetPosition,
      size: target.c.block.props,
      color,
    }).execute();

    // Update blocks view state once when there are no more throws remaining
    if (state.parcelThrowsRemaining <= 0) {
      this.updateParcelsBlocksEnabledState();
    }

    // Set the parcel to be visible again
    parcel.c.view2d.view.visible = true;

    return true;
  }

  /**
   * Update all parcels group in the grid to reflect if they can still throw new parcels (parcels)
   */
  private updateParcelsBlocksEnabledState() {
    blockIterateAll(this.scene, (base) => {
      if (base.c.block.blockId === 'parcels') {
        base.c.blockParcels.updateEnabledState();
      }
    });
  }

  private async animateParcelSpawn(to: Vector2) {
    const duration = 1;
    const fadeDuration = duration * 0.2;
    const parcels = this.options.subject;
    const parcelsView = parcels.c.view2d.view.getChildAt(0);
    const offset = config.tile.size / 2;
    const fromScale = 1;
    const toScale = 1;
    const rotationSign = Math.random() > 0.5 ? 1 : -1;

    // Wobble the parcels for a better response that they are activated
    gsap
      .timeline()
      .to(parcelsView, {
        scaleX: 0.8,
        scaleY: 1.2,
        duration: 0.1,
        ease: 'sine.out',
      })
      .to(parcelsView, {
        scaleX: 1,
        scaleY: 1,
        duration: 1.1,
        ease: 'elastic.out',
      });

    // Create the temporary parcel view for animation
    const view = new Container();
    const parachute = Sprite.from('parcels/block.parcels.parachute.png');
    const parcel = createBlockView({ id: 'hatAll' });
    parachute.anchorX = 0.5;
    parachute.anchorY = 1;
    parachute.x = offset;
    parachute.y = 20;
    view.alpha = 0;
    view.scaleX = view.scaleY = fromScale;
    view.addChild(parachute);
    view.addChild(parcel);
    this.scene.stage.addChild(view);

    const dropRatio = 0.4;
    const dropDurationRatio = dropRatio * 0.75;
    const toPosition = to.y - view.height * 0.5;
    const gridSize = config.tile.size * (this.scene.mapDef?.grid.rows ?? 9);
    const fromPosition = toPosition - gridSize;
    const dropPosition = toPosition - (toPosition - fromPosition) * dropRatio;

    // Animate the parcel fading in
    gsap.to(view, {
      alpha: 1,
      duration: fadeDuration,
      ease: 'quad.in',
    });

    // set initial view pos
    view.x = to.x;
    view.y = fromPosition;

    // fly down animation
    await gsap
      .timeline()
      .to(view, {
        y: dropPosition,
        scaleX: toScale,
        scaleY: toScale,
        duration: duration * (1 - dropDurationRatio),
        ease: 'none',
        onUpdate: () => {
          const diff = dropPosition - fromPosition;
          const ratio = (view.y - fromPosition) / diff;
          const angle = ratio * Math.PI * 2;
          const value = Math.sin(angle) * 0.1;
          view.rotation = value * rotationSign;
        },
      })
      // parachute fold animation
      .to(
        parachute,
        {
          scaleX: 0,
          scaleY: 0,
          duration: fadeDuration,
          ease: 'back.in',
        },
        duration * (1 - dropDurationRatio) - fadeDuration,
      );

    // drop down animation
    await gsap.to(view, {
      y: toPosition,
      duration: duration * dropDurationRatio,
      ease: 'none',
    });

    // Remove the temporary view
    this.scene.stage.removeChild(view);
  }

  private selectTarget(): BasicBlockEntity {
    const candidates: BlockEntity[] = [];

    // get target candidate positions
    mapIterateCells(this.scene, (cell) => {
      if (cellHasBasicAndNoOverlay(cell)) candidates.push(cell.base!.entity);
    });

    // pick a random one
    return arrayRandom(candidates) as BasicBlockEntity;
  }
}
